// Applies the Care Packages draft queue on top of the server snapshot so the
// board reflects unpublished edits immediately (mirrors applyScheduleDraft, but
// for the Care Packages data shapes). Pure: returns a new object, never mutates.
//
// Change types handled here:
//   assign     — set / clear the carer(s) on a visit (empty carerIds = unassign)
//   stopVisit  — drop a visit from the board
//
// The Care Packages payload comes in four shapes (mode × view):
//   day  · client → zones[].visits[]
//   day  · carer  → zones[].carers[].visits[] + zones[].unassigned[]
//   week · client → zones[].clients[].days{}
//   week · carer  → zones[].carers[].days{} + zones[].unassigned_days{}
// Touched items get a `__draft` flag so the UI can highlight them.

const clone = (o) => (typeof structuredClone === 'function' ? structuredClone(o) : JSON.parse(JSON.stringify(o)));
const startMin = (t) => { if (!t) return 0; const [h, m] = String(t).split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const sameId = (a, b) => String(a) === String(b);

// ── day · client ───────────────────────────────────────────────────────────
const dayClientAssign = (data, p) => {
  const targets = p.carerIds || [];
  data.zones.forEach((z) => (z.visits || []).forEach((v) => {
    if (!sameId(v.assignment_id, p.assignment_id)) return;
    if (targets.length === 0) { v.status = 'unassigned'; v.carers = []; }
    else { v.status = v.status === 'completed' ? 'completed' : 'scheduled'; v.carers = targets.map((c) => ({ id: c.id, name: c.name })); }
    v.__draft = true;
  }));
};

// ── day · carer ────────────────────────────────────────────────────────────
const dayCarerAssign = (data, p) => {
  const targets = p.carerIds || [];
  let base = null; let homeZone = null;
  data.zones.forEach((z) => {
    (z.carers || []).forEach((c) => {
      c.visits = (c.visits || []).filter((v) => {
        if (sameId(v.assignment_id, p.assignment_id)) { base = base || v; homeZone = homeZone || z; return false; }
        return true;
      });
    });
    z.unassigned = (z.unassigned || []).filter((v) => {
      if (sameId(v.assignment_id, p.assignment_id)) { base = base || v; homeZone = homeZone || z; return false; }
      return true;
    });
  });
  if (!base && p.visit) base = clone(p.visit);
  if (!base) return;
  const zone = homeZone
    || data.zones.find((z) => sameId(z.id, p.visit?.zone_id))
    || data.zones[0];
  if (!zone) return;

  if (targets.length === 0) {
    zone.unassigned = [...(zone.unassigned || []), { ...base, status: 'unassigned', __draft: true }];
    return;
  }
  const t = targets[0];
  let carer = (zone.carers || []).find((c) => sameId(c.id, t.id));
  if (!carer) {
    for (const z of data.zones) { const f = (z.carers || []).find((c) => sameId(c.id, t.id)); if (f) { carer = f; break; } }
  }
  if (carer) {
    const visit = { ...base, status: base.status === 'completed' ? 'completed' : 'scheduled', _carerId: t.id, __draft: true };
    carer.visits = [...(carer.visits || []), visit].sort((a, b) => startMin(a.start) - startMin(b.start));
  } else {
    zone.unassigned = [...(zone.unassigned || []), { ...base, status: 'unassigned', __draft: true }];
  }
};

// ── week · carer ───────────────────────────────────────────────────────────
const weekCarerAssign = (data, p) => {
  const targets = p.carerIds || [];
  const day = p.day;
  if (!day) return;
  let base = null;
  data.zones.forEach((z) => {
    const arr = (z.unassigned_days || {})[day];
    if (!arr) return;
    const idx = arr.findIndex((v) => sameId(v.assignment_id, p.assignment_id));
    if (idx >= 0) { base = base || arr[idx]; arr.splice(idx, 1); }
  });
  if (!base && p.visit) base = clone(p.visit);
  if (!base || targets.length === 0) return;
  const t = targets[0];
  data.zones.forEach((z) => {
    const carer = (z.carers || []).find((c) => sameId(c.id, t.id));
    if (!carer) return;
    carer.days = carer.days || {};
    carer.days[day] = [...(carer.days[day] || []), { ...base, status: 'scheduled', __draft: true }];
  });
};

// stopVisit — remove the visit everywhere, across all shapes.
const stopEverywhere = (data, assignmentId) => {
  data.zones.forEach((z) => {
    if (z.visits) z.visits = z.visits.filter((v) => !sameId(v.assignment_id, assignmentId));
    if (z.unassigned) z.unassigned = z.unassigned.filter((v) => !sameId(v.assignment_id, assignmentId));
    (z.carers || []).forEach((c) => {
      if (c.visits) c.visits = c.visits.filter((v) => !sameId(v.assignment_id, assignmentId));
      if (c.days) Object.keys(c.days).forEach((d) => { c.days[d] = c.days[d].filter((v) => !sameId(v.assignment_id, assignmentId)); });
    });
    (z.clients || []).forEach((cl) => {
      if (cl.days) Object.keys(cl.days).forEach((d) => { cl.days[d] = cl.days[d].filter((v) => !sameId(v.assignment_id, assignmentId)); });
    });
    if (z.unassigned_days) Object.keys(z.unassigned_days).forEach((d) => { z.unassigned_days[d] = z.unassigned_days[d].filter((v) => !sameId(v.assignment_id, assignmentId)); });
  });
};

// Keep derived counters in step so chips/badges match the drafted board.
const recompute = (data) => {
  let needAssign = 0;
  data.zones.forEach((z) => {
    (z.carers || []).forEach((c) => { if (c.visits) c.visit_count = c.visits.length; });
    if (z.visits) {
      z.visit_count = z.visits.length;
      z.unassigned_count = z.visits.filter((v) => v.status === 'unassigned').length;
      needAssign += z.unassigned_count;
    }
    if (z.unassigned) needAssign += z.unassigned.length;
    if (z.unassigned_days) needAssign += Object.values(z.unassigned_days).reduce((n, arr) => n + (arr || []).length, 0);
  });
  if (data.summary) data.summary = { ...data.summary, need_assignment: needAssign };
};

const applyOne = (data, change) => {
  const p = change.payload || {};
  const isDayClient = data._mode === 'day' && data.view === 'client';
  const isDayCarer = data._mode === 'day' && data.view === 'carer';
  const isWeekCarer = data._mode === 'week' && data.view === 'carer';
  switch (change.type) {
    case 'assign':
      if (isDayClient) dayClientAssign(data, p);
      else if (isDayCarer) dayCarerAssign(data, p);
      else if (isWeekCarer) weekCarerAssign(data, p);
      return;
    case 'stopVisit':
      stopEverywhere(data, p.assignment_id);
      return;
    default:
  }
};

export const applyPackagesDraft = (data, changes = []) => {
  if (!data || !changes.length) return data;
  const next = clone(data);
  next.zones = next.zones || [];
  changes.forEach((c) => applyOne(next, c));
  recompute(next);
  return next;
};

export default applyPackagesDraft;
